import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/services/firestore/collections';
import { withIdConverter } from '@/services/firestore/converters';
import type {
  PayAdjustment,
  PayrollRun,
  StaffMember,
  TimeClockEntry,
} from '@/services/firestore/types';

const staffConverter = withIdConverter<StaffMember>();
const timeClockConverter = withIdConverter<TimeClockEntry>();
const payrollRunConverter = withIdConverter<PayrollRun>();
const payAdjustmentConverter = withIdConverter<PayAdjustment>();

export const STAFF_QUERY_KEY = ['staff'];
export const PUBLIC_STAFF_QUERY_KEY = ['publicStaff'];
export const TIME_CLOCK_QUERY_KEY = ['timeClock'];
export const PAYROLL_RUNS_QUERY_KEY = ['payrollRuns'];
export const PAY_ADJUSTMENTS_QUERY_KEY = ['payAdjustments'];

export async function getStaffMember(staffId: string) {
  try {
    const snapshot = await getDoc(
      doc(db, COLLECTIONS.STAFF, staffId).withConverter(staffConverter),
    );

    return snapshot.exists() ? snapshot.data() : null;
  } catch (error) {
    console.error('Error fetching staff member:', error);
    throw error;
  }
}

export async function getStaffByAuthUid(authUid: string, email?: string | null) {
  const byDocId = await getStaffMember(authUid);
  if (byDocId) {
    return byDocId;
  }

  const staffCollection = collection(db, COLLECTIONS.STAFF).withConverter(staffConverter);

  const authUidQuery = query(
    staffCollection,
    where('authUid', '==', authUid),
    limit(1),
  );
  const authUidMatch = await getDocs(authUidQuery);
  if (!authUidMatch.empty) {
    return authUidMatch.docs[0].data();
  }

  const normalizedEmail = email?.trim().toLowerCase();
  if (normalizedEmail) {
    const emailQuery = query(
      staffCollection,
      where('email', '==', normalizedEmail),
      limit(1),
    );
    const emailMatch = await getDocs(emailQuery);
    if (!emailMatch.empty) {
      return emailMatch.docs[0].data();
    }
  }

  return null;
}

export async function listPublicStaff() {
  try {
    const staffQuery = query(
      collection(db, COLLECTIONS.PUBLIC_STAFF).withConverter(staffConverter),
      where('active', '==', true),
      orderBy('displayName', 'asc'),
    );
    const snapshot = await getDocs(staffQuery);

    return snapshot.docs.map((staffMember) => staffMember.data());
  } catch (error) {
    console.error('Error listing public staff:', error);
    throw error;
  }
}

export async function listActiveStaff() {
  try {
    const staffQuery = query(
      collection(db, COLLECTIONS.STAFF).withConverter(staffConverter),
      where('active', '==', true),
      orderBy('displayName', 'asc'),
    );
    const snapshot = await getDocs(staffQuery);

    return snapshot.docs.map((staffMember) => staffMember.data());
  } catch (error) {
    console.error('Error listing active staff:', error);
    throw error;
  }
}

export async function listStaff() {
  try {
    const staffQuery = query(
      collection(db, COLLECTIONS.STAFF).withConverter(staffConverter),
      orderBy('displayName', 'asc'),
    );
    const snapshot = await getDocs(staffQuery);

    return snapshot.docs.map((staffMember) => staffMember.data());
  } catch (error) {
    console.error('Error listing staff:', error);
    throw error;
  }
}

export async function listOpenTimeClockEntries() {
  try {
    const timeClockQuery = query(
      collection(db, COLLECTIONS.TIME_CLOCK).withConverter(timeClockConverter),
      where('status', 'in', ['open', 'onBreak']),
    );
    const snapshot = await getDocs(timeClockQuery);

    return snapshot.docs.map((entry) => entry.data());
  } catch (error) {
    console.error('Error listing open time clock entries:', error);
    throw error;
  }
}

export async function listTimeClockEntries(startAt: string, endAt: string, resultLimit = 100) {
  try {
    const timeClockQuery = query(
      collection(db, COLLECTIONS.TIME_CLOCK).withConverter(timeClockConverter),
      where('clockInAt', '>=', startAt),
      where('clockInAt', '<', endAt),
      orderBy('clockInAt', 'desc'),
      limit(resultLimit),
    );
    const snapshot = await getDocs(timeClockQuery);

    return snapshot.docs.map((entry) => entry.data());
  } catch (error) {
    console.error('Error listing time clock entries:', error);
    throw error;
  }
}

export async function listPayrollRuns(resultLimit = 12) {
  try {
    const payrollQuery = query(
      collection(db, COLLECTIONS.PAYROLL_RUNS).withConverter(payrollRunConverter),
      orderBy('periodEnd', 'desc'),
      limit(resultLimit),
    );
    const snapshot = await getDocs(payrollQuery);

    return snapshot.docs.map((run) => run.data());
  } catch (error) {
    console.error('Error listing payroll runs:', error);
    throw error;
  }
}

export async function listPayAdjustments(startAt: string, endAt: string, resultLimit = 100) {
  try {
    const adjustmentsQuery = query(
      collection(db, COLLECTIONS.PAY_ADJUSTMENTS).withConverter(payAdjustmentConverter),
      where('effectiveDate', '>=', startAt),
      where('effectiveDate', '<', endAt),
      orderBy('effectiveDate', 'desc'),
      limit(resultLimit),
    );
    const snapshot = await getDocs(adjustmentsQuery);

    return snapshot.docs.map((adjustment) => adjustment.data());
  } catch (error) {
    console.error('Error listing pay adjustments:', error);
    throw error;
  }
}
